import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, RefreshCw, Upload, X, FileText, Loader2 } from "lucide-react";
import { api, authHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { Panel, inputCls } from "@/components/admin/cc";
import { CategoryBadge, ScoreBadge, StatusBadge } from "@/components/admin/badges";
import { L, fmtDate } from "@/lib/labels";

const SOORTEN = [
  { key: "aanvragen", label: "Aanvragen" },
  { key: "prospecten", label: "Prospecten" },
  { key: "alle", label: "Alle" },
];
const SOURCE_PARAM = { aanvragen: undefined, prospecten: "import", alle: "all" };

export default function Leads() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [soort, setSoort] = useState(params.get("soort") || (params.get("category") === "prospect" ? "prospecten" : "aanvragen"));

  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (q) p.q = q;
      if (status) p.status = status;
      if (category) p.category = category;
      const src = SOURCE_PARAM[soort];
      if (src) p.source = src;
      const { data } = await api.get("/admin/leads", { headers: authHeaders(), params: p });
      setLeads(data);
    } catch (e) { if (e?.response?.status === 401) logout(); }
    finally { setLoading(false); }
  }, [q, status, category, soort, logout]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    const np = {};
    if (q) np.q = q;
    if (status) np.status = status;
    if (category) np.category = category;
    if (soort && soort !== "aanvragen") np.soort = soort;
    setParams(np, { replace: true });
  }, [q, status, category, soort, setParams]);

  const doImport = async () => {
    if (!file) { toast.error("Kies eerst een CSV-bestand."); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/leads/import", fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.created} toegevoegd · ${data.duplicates} duplicaten · ${data.skipped} overgeslagen`);
      setImportOpen(false);
      setFile(null);
      setSoort("prospecten");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import mislukt. Controleer het CSV-bestand.");
    } finally {
      setImporting(false);
    }
  };

  const isProspect = soort === "prospecten";
  const headers = isProspect
    ? ["Categorie", "Bedrijf", "Contact", "E-mail", "Telefoon", "Stad", "Segment"]
    : ["Score", "Categorie", "Status", "Naam", "Gemeente", "Projecttype", "m²", "Budget", "Aanvraag"];

  const actions = (
    <button onClick={() => setImportOpen(true)} data-testid="leads-import-open" className="inline-flex items-center gap-2 rounded-xl bg-cc-accent text-white px-4 py-2 font-body text-sm font-semibold hover:bg-cc-accenth transition-colors">
      <Upload className="w-4 h-4" /> Leads importeren
    </button>
  );

  return (
    <AdminLayout title="Leads" subtitle="Offerteaanvragen & geïmporteerde prospecten" actions={actions}>
      {/* Soort-schakelaar */}
      <div className="inline-flex items-center gap-1 rounded-xl border border-cc-border bg-cc-panel p-1 mb-5" data-testid="soort-switch">
        {SOORTEN.map((s) => (
          <button
            key={s.key}
            onClick={() => { setSoort(s.key); if (s.key === "prospecten") setCategory(""); }}
            data-testid={`soort-${s.key}`}
            className={`rounded-lg px-4 py-1.5 font-body text-sm font-semibold transition-colors ${soort === s.key ? "bg-cc-accent text-white" : "text-cc-sub hover:text-cc-ink"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-cc-faint" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="filter-search" placeholder="Zoek op naam, bedrijf, e-mail of gemeente…" className={`${inputCls} pl-10`} />
        </div>
        {!isProspect && (
          <select data-testid="filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} w-auto`}>
            <option value="">Alle categorieën</option>
            <option value="hot">HOT</option>
            <option value="high">High</option>
            <option value="normal">Normaal</option>
            <option value="low">Laag</option>
          </select>
        )}
        {!isProspect && (
          <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} w-auto`}>
            <option value="">Alle statussen</option>
            <option value="nieuw">Nieuw</option>
            <option value="bezocht">Bezocht</option>
            <option value="offerte_verzonden">Offerte verzonden</option>
            <option value="afgerond">Afgerond</option>
          </select>
        )}
        <button onClick={load} data-testid="leads-refresh" className="inline-flex items-center gap-2 rounded-xl border border-cc-border px-3.5 py-2.5 font-body text-sm font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <Panel className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="leads-table">
            <thead>
              <tr className="border-b border-cc-border">
                {headers.map((h) => (
                  <th key={h} className="px-4 py-3.5 font-body text-[11px] font-bold uppercase tracking-wider text-cc-faint whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={headers.length} className="px-4 py-12 text-center font-body text-cc-faint">Laden…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={headers.length} className="px-4 py-12 text-center font-body text-cc-faint" data-testid="leads-empty">{isProspect ? "Nog geen prospecten. Klik op ‘Leads importeren’." : "Geen leads gevonden."}</td></tr>
              ) : isProspect ? (
                leads.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className="border-b border-cc-border last:border-0 hover:bg-cc-panel2 transition-colors">
                    <td className="px-4 py-3"><CategoryBadge category={lead.category} /></td>
                    <td className="px-4 py-3 font-body text-sm font-medium text-cc-ink whitespace-nowrap">{lead.bedrijfsnaam || "—"}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-sub whitespace-nowrap">{lead.functie ? `${lead.voornaam} (${lead.functie})` : (lead.achternaam ? `${lead.voornaam} ${lead.achternaam}` : "—")}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-sub whitespace-nowrap">{lead.email || "—"}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-sub whitespace-nowrap">{lead.telefoon || "—"}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-sub whitespace-nowrap">{lead.gemeente || "—"}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-faint whitespace-nowrap">{lead.segment || "—"}</td>
                  </tr>
                ))
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} onClick={() => navigate(`/admin/lead/${lead.id}`)} data-testid={`lead-row-${lead.id}`} className="border-b border-cc-border last:border-0 hover:bg-cc-panel2 cursor-pointer transition-colors">
                    <td className="px-4 py-3"><ScoreBadge score={lead.score} category={lead.category} /></td>
                    <td className="px-4 py-3"><CategoryBadge category={lead.category} /></td>
                    <td className="px-4 py-3"><StatusBadge status={lead.status} /></td>
                    <td className="px-4 py-3 font-body text-sm font-medium text-cc-ink whitespace-nowrap">{lead.voornaam} {lead.achternaam}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-sub whitespace-nowrap">{lead.gemeente || "—"}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-sub whitespace-nowrap">{(lead.project_types || []).map(L.projectType).join(", ")}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-sub">{lead.oppervlakte || "—"}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-sub whitespace-nowrap">{L.budget(lead.budget)}</td>
                    <td className="px-4 py-3 font-body text-sm text-cc-faint whitespace-nowrap">{fmtDate(lead.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-5" data-testid="import-modal">
          <div className="bg-cc-panel w-full max-w-lg rounded-2xl border border-cc-border shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-cc-border">
              <h3 className="font-heading font-bold text-base text-cc-ink">Leads importeren (CSV)</h3>
              <button onClick={() => setImportOpen(false)} data-testid="import-close" className="text-cc-faint hover:text-cc-ink"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <p className="font-body text-sm text-cc-sub mb-4">
                Upload een CSV met kolommen zoals <span className="text-cc-ink font-medium">bedrijfsnaam, email, telefoon, stad, segment, contactpersoon, opmerking</span>. Rijen zonder e-mail of bedrijf worden overgeslagen; dubbele e-mails worden genegeerd.
              </p>
              <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-cc-border bg-cc-panel2/40 px-4 py-8 cursor-pointer hover:border-cc-accent transition-colors" data-testid="import-dropzone">
                <FileText className="w-8 h-8 text-cc-faint" />
                {file ? (
                  <span className="font-body text-sm font-medium text-cc-ink">{file.name}</span>
                ) : (
                  <span className="font-body text-sm text-cc-faint">Klik om een .csv-bestand te kiezen</span>
                )}
                <input type="file" accept=".csv,text/csv" className="hidden" data-testid="import-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-cc-border">
              <button onClick={() => setImportOpen(false)} className="rounded-xl border border-cc-border px-4 py-2.5 font-body text-sm font-medium text-cc-sub hover:text-cc-ink transition-colors">Annuleren</button>
              <button onClick={doImport} disabled={importing || !file} data-testid="import-submit" className="inline-flex items-center gap-2 rounded-xl bg-cc-accent text-white px-5 py-2.5 font-body text-sm font-semibold hover:bg-cc-accenth transition-colors disabled:opacity-60">
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Importeren
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
